import React, { useState, useCallback, useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, zoomPlugin);

const BASE_URL = "https://api.fortnite.com/ecosystem/v1";

const METRIC_KEYS = [
  "averageMinutesPerPlayer",
  "peakCCU",
  "favorites",
  "minutesPlayed",
  "recommendations",
  "plays",
  "uniquePlayers",
];

const METRIC_LABELS = {
  averageMinutesPerPlayer: "Avg Min/Player",
  peakCCU: "Peak CCU",
  favorites: "Favorites",
  minutesPlayed: "Minutes Played",
  recommendations: "Recommendations",
  plays: "Plays",
  uniquePlayers: "Unique Players",
  retentionD1: "Retention D1",
  retentionD7: "Retention D7",
};

const METRIC_COLORS = {
  averageMinutesPerPlayer: "#FCE865",
  peakCCU: "#3b82f6",
  favorites: "#f43f5e",
  minutesPlayed: "#10b981",
  recommendations: "#a855f7",
  plays: "#f97316",
  uniquePlayers: "#06b6d4",
  retentionD1: "#ec4899",
  retentionD7: "#8b5cf6",
};

const API_ENDPOINTS = [
  {
    id: "list-islands",
    label: "List Islands",
    method: "GET",
    path: "/islands",
    description: "Retrieves a sorted list of islands (newest first).",
    params: [
      { name: "after", type: "text", label: "After Cursor", required: false, placeholder: "Cursor for next page" },
      { name: "before", type: "text", label: "Before Cursor", required: false, placeholder: "Cursor for previous page" },
      { name: "size", type: "number", label: "Page Size", required: false, placeholder: "100" },
    ],
  },
  {
    id: "island-metadata",
    label: "Island Metadata",
    method: "GET",
    path: "/islands/{code}",
    description: "Retrieves metadata for an island code.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
    ],
  },
  {
    id: "island-metrics",
    label: "Island Metrics (Day)",
    method: "GET",
    path: "/islands/{code}/metrics",
    description: "Retrieves all usage metrics for an island with day interval buckets.",
    hasChart: true,
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "island-metrics-interval",
    label: "Island Metrics (Custom Interval)",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}",
    description: "Retrieves usage metrics at a specified interval. Retention is day-only. Avg minutes/player is day & hour only.",
    hasChart: true,
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      {
        name: "metrics", type: "multiselect", label: "Metrics (optional)", required: false,
        options: ["averageMinutesPerPlayer", "peakCCU", "favorites", "minutesPlayed", "recommendations", "plays", "uniquePlayers", "retention"],
      },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "peak-ccu",
    label: "Peak CCU",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}/peak-ccu",
    description: "Retrieves peak concurrent players for the island.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "favorites",
    label: "Favorites",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}/favorites",
    description: "Retrieves the number of times the island was favorited.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "minutes-played",
    label: "Minutes Played",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}/minutes-played",
    description: "Retrieves total minutes players spent on the island.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "avg-minutes",
    label: "Avg Minutes Per Player",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}/average-minutes-per-player",
    description: "Retrieves average minutes per player. Only available for day intervals.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "recommendations",
    label: "Recommendations",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}/recommendations",
    description: "Retrieves the number of times the island was recommended.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "unique-players",
    label: "Unique Players",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}/unique-players",
    description: "Retrieves the number of unique players.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "plays",
    label: "Plays",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}/plays",
    description: "Retrieves the number of play sessions.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
  {
    id: "retention",
    label: "Retention",
    method: "GET",
    path: "/islands/{code}/metrics/{interval}/retention",
    description: "Retrieves D1 and D7 retention. Only available for day intervals.",
    params: [
      { name: "code", type: "text", label: "Island Code", required: true, placeholder: "1234-1234-1234", pathParam: true },
      { name: "interval", type: "select", label: "Interval", required: true, options: ["day", "hour", "minute"], pathParam: true },
      { name: "from", type: "datetime-local", label: "From", required: false },
      { name: "to", type: "datetime-local", label: "To", required: false },
    ],
  },
];

function toISOString(localDatetime) {
  if (!localDatetime) return null;
  return new Date(localDatetime).toISOString();
}

function buildUrl(endpoint, values) {
  let path = endpoint.path;
  const queryParams = [];

  for (const param of endpoint.params) {
    const val = values[param.name];
    if (!val || (Array.isArray(val) && val.length === 0)) continue;

    if (param.pathParam) {
      path = path.replace(`{${param.name}}`, encodeURIComponent(val));
    } else if (param.type === "datetime-local") {
      queryParams.push(`${param.name}=${encodeURIComponent(toISOString(val))}`);
    } else if (param.type === "multiselect" && Array.isArray(val)) {
      for (const v of val) {
        queryParams.push(`metrics=${encodeURIComponent(v)}`);
      }
    } else {
      queryParams.push(`${param.name}=${encodeURIComponent(val)}`);
    }
  }

  const qs = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
  return `${BASE_URL}${path}${qs}`;
}

function isMetricsData(data) {
  if (!data || typeof data !== "object") return false;
  return METRIC_KEYS.some((k) => Array.isArray(data[k])) || Array.isArray(data.retention);
}

function mergeMetricsData(base, overlay) {
  const result = { ...base };
  const allKeys = [...METRIC_KEYS, "retention"];

  for (const key of allKeys) {
    const baseArr = base[key] || [];
    const overlayArr = overlay[key] || [];
    if (baseArr.length === 0 && overlayArr.length === 0) continue;

    const map = new Map();
    for (const item of baseArr) map.set(item.timestamp, item);
    for (const item of overlayArr) map.set(item.timestamp, item);

    result[key] = Array.from(map.values()).sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );
  }
  return result;
}

function MetricsChart({ data, visibleMetrics }) {
  const chartRef = useRef(null);

  const allTimestamps = useMemo(() => {
    const tsSet = new Set();
    for (const key of [...METRIC_KEYS, "retention"]) {
      const arr = data[key];
      if (Array.isArray(arr)) {
        for (const item of arr) tsSet.add(item.timestamp);
      }
    }
    return Array.from(tsSet).sort((a, b) => new Date(a) - new Date(b));
  }, [data]);

  const labels = useMemo(
    () =>
      allTimestamps.map((ts) => {
        const d = new Date(ts);
        const month = String(d.getUTCMonth() + 1).padStart(2, "0");
        const day = String(d.getUTCDate()).padStart(2, "0");
        const hour = String(d.getUTCHours()).padStart(2, "0");
        const min = String(d.getUTCMinutes()).padStart(2, "0");
        if (hour === "00" && min === "00") return `${month}/${day}`;
        return `${month}/${day} ${hour}:${min}`;
      }),
    [allTimestamps]
  );

  const datasets = useMemo(() => {
    const ds = [];

    for (const key of METRIC_KEYS) {
      if (!visibleMetrics[key]) continue;
      const arr = data[key];
      if (!Array.isArray(arr) || arr.length === 0) continue;
      const valMap = new Map(arr.map((item) => [item.timestamp, item.value]));
      ds.push({
        label: METRIC_LABELS[key],
        data: allTimestamps.map((ts) => valMap.get(ts) ?? null),
        borderColor: METRIC_COLORS[key],
        backgroundColor: METRIC_COLORS[key] + "20",
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 6,
        spanGaps: true,
      });
    }

    if (Array.isArray(data.retention) && data.retention.length > 0) {
      const retMap = new Map(data.retention.map((item) => [item.timestamp, item]));
      if (visibleMetrics.retentionD1) {
        ds.push({
          label: METRIC_LABELS.retentionD1,
          data: allTimestamps.map((ts) => retMap.get(ts)?.d1 ?? null),
          borderColor: METRIC_COLORS.retentionD1,
          backgroundColor: METRIC_COLORS.retentionD1 + "20",
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
          spanGaps: true,
        });
      }
      if (visibleMetrics.retentionD7) {
        ds.push({
          label: METRIC_LABELS.retentionD7,
          data: allTimestamps.map((ts) => retMap.get(ts)?.d7 ?? null),
          borderColor: METRIC_COLORS.retentionD7,
          backgroundColor: METRIC_COLORS.retentionD7 + "20",
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
          spanGaps: true,
        });
      }
    }

    return ds;
  }, [data, allTimestamps, visibleMetrics]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#18181B",
          borderColor: "#27272A",
          borderWidth: 1,
          titleColor: "#e5e7eb",
          bodyColor: "#d1d5db",
          padding: 12,
        },
        zoom: {
          pan: { enabled: true, mode: "x" },
          zoom: {
            wheel: { enabled: true },
            pinch: { enabled: true },
            mode: "x",
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#9ca3af", maxRotation: 45 },
          grid: { color: "#27272A" },
        },
        y: {
          ticks: { color: "#9ca3af" },
          grid: { color: "#27272A" },
        },
      },
    }),
    []
  );

  const handleResetZoom = () => {
    chartRef.current?.resetZoom();
  };

  if (datasets.length === 0) {
    return <p className="text-gray-500 text-sm">No metrics selected or no data available.</p>;
  }

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={handleResetZoom}
          className="px-3 py-1 text-xs bg-[#27272A] hover:bg-[#3f3f46] text-gray-400 rounded transition-colors"
        >
          Reset Zoom
        </button>
      </div>
      <div className="h-[320px] sm:h-[400px]">
        <Line ref={chartRef} data={{ labels, datasets }} options={options} />
      </div>
      <p className="text-xs text-gray-600 mt-2">Scroll to zoom, drag to pan.</p>
    </div>
  );
}

export function FortniteApiPage() {
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [requestUrl, setRequestUrl] = useState(null);

  // Chart data state
  const [chartData, setChartData] = useState(null);
  const [visibleMetrics, setVisibleMetrics] = useState(() => {
    const initial = {};
    for (const k of METRIC_KEYS) initial[k] = true;
    initial.retentionD1 = true;
    initial.retentionD7 = true;
    return initial;
  });

  // Paste / merge JSON
  const [pasteJson, setPasteJson] = useState("");
  const [pasteError, setPasteError] = useState(null);
  const [showPastePanel, setShowPastePanel] = useState(false);

  const handleEndpointSelect = useCallback((ep) => {
    setSelectedEndpoint(ep);
    setParamValues({});
    setResponse(null);
    setError(null);
    setRequestUrl(null);
    setChartData(null);
    setPasteJson("");
    setPasteError(null);
    setShowPastePanel(false);
  }, []);

  const handleParamChange = useCallback((name, value) => {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleMultiselectToggle = useCallback((name, option) => {
    setParamValues((prev) => {
      const current = prev[name] || [];
      const next = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [name]: next };
    });
  }, []);

  const handleSend = useCallback(async () => {
    if (!selectedEndpoint) return;

    for (const param of selectedEndpoint.params) {
      if (param.required && !paramValues[param.name]) {
        setError(`"${param.label}" is required.`);
        return;
      }
    }

    const url = buildUrl(selectedEndpoint, paramValues);
    setRequestUrl(url);
    setLoading(true);
    setError(null);
    setResponse(null);
    setChartData(null);

    try {
      const res = await fetch(url);
      const contentType = res.headers.get("content-type") || "";
      let data;
      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        data = await res.text();
      }

      if (!res.ok) {
        setError(`HTTP ${res.status}: ${typeof data === "object" ? data.errorMessage || JSON.stringify(data) : data}`);
      } else {
        setResponse(data);
        if (selectedEndpoint.hasChart && isMetricsData(data)) {
          setChartData(data);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedEndpoint, paramValues]);

  const handleDownload = useCallback(() => {
    const downloadData = chartData || response;
    if (!downloadData) return;
    const json = JSON.stringify(downloadData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fortnite-api-${selectedEndpoint?.id || "response"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [chartData, response, selectedEndpoint]);

  const handleLoadPasteJson = useCallback(() => {
    setPasteError(null);
    try {
      const parsed = JSON.parse(pasteJson);
      if (!isMetricsData(parsed)) {
        setPasteError("JSON does not contain valid metrics data.");
        return;
      }
      setChartData(parsed);
      setResponse(parsed);
    } catch {
      setPasteError("Invalid JSON.");
    }
  }, [pasteJson]);

  const handleMergeJson = useCallback(() => {
    setPasteError(null);
    if (!chartData) {
      setPasteError("No existing data to merge with. Load or fetch data first.");
      return;
    }
    try {
      const parsed = JSON.parse(pasteJson);
      if (!isMetricsData(parsed)) {
        setPasteError("Pasted JSON does not contain valid metrics data.");
        return;
      }
      const merged = mergeMetricsData(chartData, parsed);
      setChartData(merged);
      setResponse(merged);
    } catch {
      setPasteError("Invalid JSON.");
    }
  }, [pasteJson, chartData]);

  const toggleMetric = useCallback((key) => {
    setVisibleMetrics((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const [copied, setCopied] = useState(false);

  const handleCopyJson = useCallback(() => {
    const copyData = chartData || response;
    if (!copyData) return;
    navigator.clipboard.writeText(JSON.stringify(copyData, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [chartData, response]);

  const showChart = selectedEndpoint?.hasChart;
  const allMetricToggleKeys = useMemo(() => {
    if (!chartData) return [];
    const keys = [];
    for (const k of METRIC_KEYS) {
      if (Array.isArray(chartData[k]) && chartData[k].length > 0) keys.push(k);
    }
    if (Array.isArray(chartData.retention) && chartData.retention.length > 0) {
      keys.push("retentionD1", "retentionD7");
    }
    return keys;
  }, [chartData]);

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-200">Fortnite Data API</h2>
        <p className="mt-2 text-sm text-gray-400">
          Query the public Fortnite Data API for island metrics and performance data.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Endpoint Selector */}
        <div className="lg:col-span-1 bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Endpoints</h3>
          <div className="space-y-1">
            {API_ENDPOINTS.map((ep) => (
              <button
                key={ep.id}
                onClick={() => handleEndpointSelect(ep)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedEndpoint?.id === ep.id
                    ? "bg-fortpixel-gold text-black font-medium"
                    : "text-gray-300 hover:bg-[#27272A]"
                }`}
              >
                <span className="inline-block w-12 text-xs font-mono opacity-60 mr-1">GET</span>
                {ep.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Panel */}
        <div className="lg:col-span-3 space-y-6">
          {!selectedEndpoint ? (
            <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4 sm:p-6 text-center text-gray-500">
              Select an endpoint from the list to get started.
            </div>
          ) : (
            <>
              {/* Endpoint Info & Params */}
              <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4 sm:p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-200">{selectedEndpoint.label}</h3>
                  <p className="text-sm text-gray-400 mt-1">{selectedEndpoint.description}</p>
                  <code className="block mt-2 text-xs text-gray-500 bg-[#09090B] rounded px-3 py-2 overflow-x-auto">
                    GET {selectedEndpoint.path}
                  </code>
                </div>

                {selectedEndpoint.params.length > 0 && (
                  <div className="space-y-4">
                    {selectedEndpoint.params.map((param) => (
                      <div key={param.name}>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          {param.label}
                          {param.required && <span className="text-red-400 ml-1">*</span>}
                        </label>

                        {param.type === "select" ? (
                          <select
                            value={paramValues[param.name] || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            className="w-full px-3 py-2 bg-[#27272A] border border-gray-700 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
                          >
                            <option value="">Select...</option>
                            {param.options.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : param.type === "multiselect" ? (
                          <div className="flex flex-wrap gap-2">
                            {param.options.map((opt) => {
                              const selected = (paramValues[param.name] || []).includes(opt);
                              return (
                                <button
                                  key={opt}
                                  onClick={() => handleMultiselectToggle(param.name, opt)}
                                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    selected
                                      ? "bg-fortpixel-gold text-black"
                                      : "bg-[#27272A] text-gray-400 hover:text-gray-200"
                                  }`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <input
                            type={param.type}
                            value={paramValues[param.name] || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            placeholder={param.placeholder || ""}
                            className="w-full px-3 py-2 bg-[#27272A] border border-gray-700 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mt-6">
                  <button
                    onClick={handleSend}
                    disabled={loading}
                    className="px-6 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium rounded-md transition-colors disabled:opacity-50"
                  >
                    {loading ? "Sending..." : "Send Request"}
                  </button>

                  {showChart && (
                    <button
                      onClick={() => setShowPastePanel((v) => !v)}
                      className="px-4 py-2 bg-[#27272A] hover:bg-[#3f3f46] text-gray-300 text-sm rounded-md transition-colors"
                    >
                      {showPastePanel ? "Hide Paste JSON" : "Paste / Merge JSON"}
                    </button>
                  )}
                </div>
              </div>

              {/* Paste JSON Panel */}
              {showChart && showPastePanel && (
                <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4 sm:p-6">
                  <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                    Paste JSON Data
                  </h3>
                  <textarea
                    value={pasteJson}
                    onChange={(e) => setPasteJson(e.target.value)}
                    placeholder='Paste Fortnite metrics JSON here...'
                    rows={8}
                    className="w-full px-3 py-2 bg-[#09090B] border border-gray-700 rounded-md text-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fortpixel-gold resize-y"
                  />
                  {pasteError && (
                    <p className="text-sm text-red-400 mt-2">{pasteError}</p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-3">
                    <button
                      onClick={handleLoadPasteJson}
                      className="w-full sm:w-auto px-4 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium text-sm rounded-md transition-colors"
                    >
                      Load as Chart Data
                    </button>
                    <button
                      onClick={handleMergeJson}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-md transition-colors"
                    >
                      Merge with Current Data
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    "Load" replaces current chart data. "Merge" combines pasted data with existing data (deduplicates by timestamp).
                  </p>
                </div>
              )}

              {/* Request URL */}
              {requestUrl && (
                <div className="bg-[#09090B] rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Request URL</p>
                  <code className="text-xs text-gray-400 break-all">{requestUrl}</code>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-xl p-4">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Chart */}
              {showChart && chartData && (
                <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4 sm:p-6">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Chart</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleCopyJson}
                        className="w-full sm:w-auto px-4 py-1.5 bg-[#27272A] hover:bg-[#3f3f46] text-gray-300 text-sm rounded-md transition-colors"
                      >
                        {copied ? "Copied!" : "Copy JSON"}
                      </button>
                      <button
                        onClick={handleDownload}
                        className="w-full sm:w-auto px-4 py-1.5 bg-[#27272A] hover:bg-[#3f3f46] text-gray-300 text-sm rounded-md transition-colors"
                      >
                        Download JSON
                      </button>
                    </div>
                  </div>

                  {/* Metric toggles */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {allMetricToggleKeys.map((key) => (
                      <button
                        key={key}
                        onClick={() => toggleMetric(key)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                          visibleMetrics[key]
                            ? "border-transparent text-black"
                            : "border-gray-700 text-gray-500"
                        }`}
                        style={{
                          backgroundColor: visibleMetrics[key] ? METRIC_COLORS[key] : "transparent",
                        }}
                      >
                        {METRIC_LABELS[key]}
                      </button>
                    ))}
                  </div>

                  <MetricsChart data={chartData} visibleMetrics={visibleMetrics} />
                </div>
              )}

              {/* Response JSON */}
              {response && (
                <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4 sm:p-6">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Response</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleCopyJson}
                        className="w-full sm:w-auto px-4 py-1.5 bg-[#27272A] hover:bg-[#3f3f46] text-gray-300 text-sm rounded-md transition-colors"
                      >
                        {copied ? "Copied!" : "Copy JSON"}
                      </button>
                      {!showChart && (
                        <button
                          onClick={handleDownload}
                          className="w-full sm:w-auto px-4 py-1.5 bg-[#27272A] hover:bg-[#3f3f46] text-gray-300 text-sm rounded-md transition-colors"
                        >
                          Download JSON
                        </button>
                      )}
                    </div>
                  </div>
                  <pre className="bg-[#09090B] rounded-lg p-4 text-xs sm:text-sm text-gray-300 overflow-auto max-h-[600px] whitespace-pre-wrap">
                    {JSON.stringify(response, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
